import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fmtIST, fmtDateIST, inr, nowLocalInput, istLocalInput } from "@/lib/dates";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Trash2, Truck, PackageCheck, Camera, ImagePlus, X, Archive, ArchiveRestore } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import OrderTimeline from "@/components/OrderTimeline";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Picked Up", "In Transit", "Delivered", "Returned to Client"];
const TYPES = ["Normal", "Express"];

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900" data-testid={testId}>{value ?? "—"}</div>
    </div>
  );
}

export default function OrderDetailsPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isClient = user?.role === "client";
  const isAdmin = user?.role === "admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverPhotos, setDeliverPhotos] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [deliverAt, setDeliverAt] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [transiting, setTransiting] = useState(false);
  const [photoKind, setPhotoKind] = useState("pickup");
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photosGalleryRef = useRef(null);
  const photosCameraRef = useRef(null);
  const deliverGalleryRef = useRef(null);
  const deliverCameraRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setData(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (location.state?.edit && data && !isClient) openEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const openEdit = () => {
    const o = data.order;
    setForm({
      client_id: o.client_id,
      bdo_reference_no: o.bdo_reference_no || "",
      passenger_name: o.passenger_name,
      passenger_number: o.passenger_number,
      number_of_bags: String(o.number_of_bags),
      delivery_location: o.delivery_location,
      delivery_type: o.delivery_type,
      delivery_status: o.delivery_status,
      amount: o.amount != null ? String(o.amount) : "",
      order_date: istLocalInput(o.created_at),
      delivered_at: o.delivered_at ? istLocalInput(o.delivered_at) : "",
    });
    if (isAdmin) api.get("/clients/options").then((r) => setClients(r.data)).catch(() => {});
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        bdo_reference_no: form.bdo_reference_no,
        passenger_name: form.passenger_name,
        passenger_number: form.passenger_number,
        number_of_bags: parseInt(form.number_of_bags, 10),
        delivery_location: form.delivery_location,
        delivery_type: form.delivery_type,
        delivery_status: form.delivery_status,
      };
      if (isAdmin) payload.client_id = form.client_id;
      if (isAdmin && form.amount !== "" && !Number.isNaN(parseFloat(form.amount))) payload.amount = parseFloat(form.amount);
      if (form.order_date) payload.order_date = form.order_date;
      if (o.delivery_status === "Delivered" && form.delivered_at) payload.delivered_at = form.delivered_at;
      await api.put(`/orders/${id}`, payload);
      toast.success("Order updated successfully.");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted.");
      navigate("/orders");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/orders/${id}/notes`, { text: noteText.trim() });
      setNoteText("");
      toast.success("Note added.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAddingNote(false);
    }
  };

  const markInTransit = async () => {
    setTransiting(true);
    try {
      await api.put(`/orders/${id}`, { delivery_status: "In Transit" });
      toast.success("Order marked as In Transit.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTransiting(false);
    }
  };

  const addDeliverPhotos = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) setDeliverPhotos((p) => [...p, ...files].slice(0, 10));
  };

  const confirmDeliver = async () => {
    setDelivering(true);
    try {
      if (deliverPhotos.length) {
        const fd = new FormData();
        deliverPhotos.forEach((f) => fd.append("files", f));
        await api.post(`/orders/${id}/images?kind=delivery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await api.put(`/orders/${id}`, { delivery_status: "Delivered", delivered_at: deliverAt || undefined });
      toast.success("Order marked as Delivered.");
      setDeliverOpen(false);
      setDeliverPhotos([]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDelivering(false);
    }
  };

  const imageUrl = (img) => `${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/images/${img.img_id}`;

  const toggleClose = async () => {
    setClosing(true);
    try {
      await api.post(`/orders/${id}/close`, { closed: !data.order.is_closed });
      toast.success(data.order.is_closed ? "Order reopened." : "Order closed — it is now hidden from the client portal.");
      setCloseOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setClosing(false);
    }
  };

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await api.post(`/orders/${id}/images?kind=${photoKind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} photo(s) added.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploadingPhotos(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24" data-testid="order-details-loading"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (notFound || !data) {
    return (
      <div className="text-center py-24" data-testid="order-not-found">
        <h2 className="text-xl font-semibold text-slate-800">Order not found</h2>
        <p className="text-sm text-slate-500 mt-2">This order does not exist or you do not have access to it.</p>
        <Button variant="outline" className="mt-6 rounded-sm" onClick={() => navigate(-1)} data-testid="order-not-found-back">Go Back</Button>
      </div>
    );
  }

  const o = data.order;

  return (
    <div className="space-y-5" data-testid="order-details-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="order-back-button" className="p-2 rounded-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors duration-150">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="order-details-title">{o.order_id}</h1>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status={o.delivery_status} />
              {o.is_closed && (
                <span data-testid="closed-badge" className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border bg-slate-700 text-white border-slate-700">
                  Closed
                </span>
              )}
            </div>
          </div>
        </div>
        {!isClient && (
          <div className="flex gap-2 flex-wrap">
            {o.delivery_status === "Picked Up" && (
              <Button onClick={markInTransit} disabled={transiting} data-testid="mark-in-transit-button" className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">
                {transiting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />} Out for Delivery
              </Button>
            )}
            {o.delivery_status === "In Transit" && (
              <Button onClick={() => { setDeliverAt(nowLocalInput()); setDeliverOpen(true); }} data-testid="mark-delivered-button" className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
                <PackageCheck className="h-4 w-4 mr-2" /> Mark Delivered
              </Button>
            )}
            <Button onClick={openEdit} data-testid="edit-order-button" className="bg-primary hover:bg-[#152D53] rounded-sm">
              <Pencil className="h-4 w-4 mr-2" /> Edit Order
            </Button>
            <Button
              onClick={() => setCloseOpen(true)}
              data-testid="close-order-button"
              variant="outline"
              className={`rounded-sm ${o.is_closed ? "border-slate-400 text-slate-700 hover:bg-slate-100" : "border-slate-500 text-slate-700 hover:bg-slate-700 hover:text-white"}`}
            >
              {o.is_closed ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              {o.is_closed ? "Reopen Order" : "Close Order"}
            </Button>
            {isAdmin && (
              <Button onClick={() => setDeleteOpen(true)} data-testid="delete-order-button" variant="outline" className="border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10 rounded-sm">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="order-info-card">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Order Information</h3>
          <Field label="Order ID" value={o.order_id} testId="detail-order-id" />
          <Field label="Client" value={o.client_name} testId="detail-client" />
          <Field label="BDO / Reference No." value={o.bdo_reference_no || "—"} testId="detail-reference" />
          <Field label="Created By" value={o.created_by_name} testId="detail-created-by" />
          <Field label="Created Date" value={fmtDateIST(o.created_at)} testId="detail-created-at" />
          <Field label="Last Updated" value={fmtDateIST(o.updated_at)} testId="detail-updated-at" />
          <Field label="Billing Status" value={o.billed ? `Billed${o.billed_at ? ` · ${fmtDateIST(o.billed_at)}` : ""}` : "Not billed"} testId="detail-billed" />
          <Field label="Payment Status" value={o.paid ? `Paid${o.paid_at ? ` · ${fmtDateIST(o.paid_at)}` : ""}` : "Unpaid"} testId="detail-paid" />
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="passenger-info-card">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Passenger Information</h3>
          <Field label="Passenger Name" value={o.passenger_name} testId="detail-passenger-name" />
          <Field label="Passenger Number" value={o.passenger_number} testId="detail-passenger-number" />
          <Field label="Number of Bags" value={o.number_of_bags} testId="detail-bags" />
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="delivery-info-card">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Delivery Information</h3>
          <Field label="Delivery Location" value={o.delivery_location} testId="detail-location" />
          <Field label="Delivery Type" value={o.delivery_type} testId="detail-type" />
          <Field label="Current Status" value={o.delivery_status} testId="detail-status" />
          {o.delivered_at && <Field label="Delivered On" value={fmtIST(o.delivered_at)} testId="detail-delivered-at" />}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="billing-info-card">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Billing</h3>
          {o.amount != null ? (
            <>
              <Field label="Amount" value={inr(o.amount)} testId="detail-amount" />
              <Field label="GST (18%)" value={inr(o.gst_amount)} testId="detail-gst" />
              <Field label="Total (incl. GST)" value={inr(o.total_amount)} testId="detail-total" />
            </>
          ) : (
            <p className="text-sm text-slate-500" data-testid="billing-not-set">Amount not set yet.{isAdmin ? " Use Edit Order to add it." : ""}</p>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="status-timeline-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-6">Status Timeline</h3>
        <OrderTimeline history={o.status_history || []} currentStatus={o.delivery_status} />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="order-photos-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Photos ({(o.images || []).length})</h3>
          {!isClient && (
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={photosGalleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="photos-gallery-input" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
              <input ref={photosCameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="photos-camera-input" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
              <Select value={photoKind} onValueChange={setPhotoKind}>
                <SelectTrigger data-testid="photo-kind-select" className="h-9 w-32 border-slate-300 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={uploadingPhotos} onClick={() => photosGalleryRef.current?.click()} data-testid="add-photos-button" className="rounded-sm border-slate-300 h-9">
                {uploadingPhotos ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ImagePlus className="h-4 w-4 mr-1" />} Add Photos
              </Button>
              <Button variant="outline" size="sm" disabled={uploadingPhotos} onClick={() => photosCameraRef.current?.click()} data-testid="photos-open-camera" className="rounded-sm border-slate-300 h-9">
                <Camera className="h-4 w-4 mr-1" /> Camera
              </Button>
            </div>
          )}
        </div>
        {(o.images || []).length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="photos-empty">No photos yet.{!isClient ? " Use Add Photos or Camera to upload pickup photos (multiple supported)." : ""}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {o.images.map((img) => (
              <a key={img.img_id} href={imageUrl(img)} target="_blank" rel="noreferrer" className="relative group" data-testid={`order-photo-${img.img_id}`}>
                <img src={imageUrl(img)} alt={img.original_filename || "Order photo"} className="h-28 w-full object-cover rounded-sm border border-slate-200 group-hover:opacity-90 transition-opacity duration-150" />
                <span className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold text-white ${img.kind === "delivery" ? "bg-green-600" : img.kind === "pickup" ? "bg-[#1B3A6B]" : "bg-slate-600"}`}>
                  {img.kind === "delivery" ? "Delivery" : img.kind === "pickup" ? "Pickup" : "Photo"}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="order-notes-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-5 py-4 border-b border-slate-200">Notes</h3>
        <div className="p-5 space-y-4">
          {(o.notes || []).length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="notes-empty">No notes yet. Add one below.</p>
          ) : (
            <div className="space-y-3">
              {o.notes.map((n, i) => (
                <div key={i} className="flex gap-3" data-testid={`note-${i}`}>
                  <div className="h-8 w-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                    {n.by_name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-800">{n.by_name}</span>
                      <span className="capitalize"> ({n.by_role})</span> · {fmtDateIST(n.at)}
                    </div>
                    <p className="text-sm text-slate-700 mt-0.5 break-words">{n.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note about this order..."
              data-testid="note-input"
              maxLength={500}
              className="border-slate-300 rounded-sm"
            />
            <Button
              onClick={addNote}
              disabled={addingNote || !noteText.trim()}
              data-testid="add-note-button"
              className="bg-primary hover:bg-[#152D53] rounded-sm shrink-0"
            >
              {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md" data-testid="audit-history-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-5 py-4 border-b border-slate-200">Activity History</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">User</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Action</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Field</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Old Value</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">New Value</th>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Date / Time</th>
              </tr>
            </thead>
            <tbody>
              {[...data.audit].reverse().map((a) => (
                <tr key={a.id} className="border-b border-slate-100 even:bg-slate-50/60" data-testid={`audit-row-${a.id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800 whitespace-nowrap">{a.user_name}</td>
                  <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{a.action}</td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{a.field || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{a.old_value || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-800 whitespace-nowrap">{a.new_value || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs tnum whitespace-nowrap">{fmtDateIST(a.at)}</td>
                </tr>
              ))}
              {data.audit.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">No activity recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mark Delivered dialog */}
      <Dialog open={deliverOpen} onOpenChange={(v) => { setDeliverOpen(v); if (!v) setDeliverPhotos([]); }}>
        <DialogContent data-testid="deliver-dialog">
          <DialogHeader>
            <DialogTitle>Mark {o.order_id} as Delivered</DialogTitle>
            <DialogDescription>Add delivery proof photos (multiple supported) and set the delivery date &amp; time — it defaults to the current time on your device.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Delivery Date &amp; Time</Label>
              <input
                type="datetime-local"
                value={deliverAt}
                onChange={(e) => setDeliverAt(e.target.value)}
                data-testid="deliver-datetime"
                className="h-10 w-full border border-slate-300 rounded-sm px-3 text-sm"
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input ref={deliverCameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="deliver-camera-input" onChange={(e) => { addDeliverPhotos(e.target.files); e.target.value = ""; }} />
              <input ref={deliverGalleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="deliver-gallery-input" onChange={(e) => { addDeliverPhotos(e.target.files); e.target.value = ""; }} />
              <Button type="button" variant="outline" onClick={() => deliverCameraRef.current?.click()} data-testid="deliver-open-camera" className="rounded-sm border-slate-300">
                <Camera className="h-4 w-4 mr-2" /> Open Camera
              </Button>
              <Button type="button" variant="outline" onClick={() => deliverGalleryRef.current?.click()} data-testid="deliver-add-gallery" className="rounded-sm border-slate-300">
                <ImagePlus className="h-4 w-4 mr-2" /> Add from Gallery
              </Button>
            </div>
            {deliverPhotos.length > 0 && (
              <div className="grid grid-cols-3 gap-3" data-testid="deliver-photo-previews">
                {deliverPhotos.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={URL.createObjectURL(f)} alt={f.name} className="h-20 w-full object-cover rounded-sm border border-slate-200" />
                    <button type="button" onClick={() => setDeliverPhotos((p) => p.filter((_, x) => x !== i))} data-testid={`deliver-remove-photo-${i}`} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#E63946] text-white flex items-center justify-center">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeliverOpen(false); setDeliverPhotos([]); }} data-testid="deliver-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={confirmDeliver} disabled={delivering} data-testid="deliver-confirm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
              {delivering ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-order-dialog">
          <DialogHeader><DialogTitle>Edit Order {o.order_id}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="edit-client" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                      {!clients.some((c) => c.id === form.client_id) && (
                        <SelectItem value={form.client_id}>{o.client_name}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">BDO / Reference No.</Label>
                <Input value={form.bdo_reference_no} onChange={(e) => setForm({ ...form, bdo_reference_no: e.target.value })} data-testid="edit-reference" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Passenger Name</Label>
                <Input value={form.passenger_name} onChange={(e) => setForm({ ...form, passenger_name: e.target.value })} data-testid="edit-passenger-name" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Passenger Number</Label>
                <Input value={form.passenger_number} onChange={(e) => setForm({ ...form, passenger_number: e.target.value })} data-testid="edit-passenger-number" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Number of Bags</Label>
                <Input type="number" min="1" value={form.number_of_bags} onChange={(e) => setForm({ ...form, number_of_bags: e.target.value })} data-testid="edit-bags" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Delivery Location</Label>
                <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} data-testid="edit-location" className="border-slate-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Delivery Type</Label>
                <Select value={form.delivery_type} onValueChange={(v) => setForm({ ...form, delivery_type: v })}>
                  <SelectTrigger data-testid="edit-type" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Delivery Status</Label>
                <Select value={form.delivery_status} onValueChange={(v) => setForm({ ...form, delivery_status: v })}>
                  <SelectTrigger data-testid="edit-status" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Order Date</Label>
                <input type="datetime-local" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })}
                  data-testid="edit-order-date" max={istLocalInput(new Date())}
                  className="h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Delivery Date &amp; Time</Label>
                <input type="datetime-local" value={form.delivered_at} onChange={(e) => setForm({ ...form, delivered_at: e.target.value })}
                  data-testid="edit-delivered-at" disabled={o.delivery_status !== "Delivered"}
                  className={`h-10 w-full border rounded-sm px-3 text-sm ${o.delivery_status !== "Delivered" ? "border-slate-200 bg-slate-50 text-slate-400" : "border-slate-300 bg-white"}`} />
                {o.delivery_status !== "Delivered" && <p className="text-xs text-slate-400">Available once the order is delivered.</p>}
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Amount (₹)</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="edit-amount" placeholder="e.g. 2500" className="border-slate-300 rounded-sm" />
                  <p className="text-xs text-slate-400">18% GST is added automatically.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-cancel" className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save" className="bg-primary hover:bg-[#152D53] rounded-sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={o.is_closed ? `Reopen ${o.order_id}?` : `Close ${o.order_id}?`}
        description={o.is_closed
          ? "The order will become visible in the client portal again."
          : "Closed orders are hidden from the client portal. Admin and staff can still see them."}
        confirmLabel={closing ? "Please wait..." : o.is_closed ? "Reopen Order" : "Close Order"}
        onConfirm={toggleClose}
        testId="close-order-dialog"
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this order?"
        description={`Order ${o.order_id} will be permanently deleted. This action is recorded in the audit log.`}
        confirmLabel="Delete Order"
        destructive
        onConfirm={confirmDelete}
        testId="delete-order-details-dialog"
      />
    </div>
  );
}
